import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Alert, Button, Space, Typography, Spin, message } from "antd";
import useSWR from "swr";
import {
  createAdminComment,
  createAdminReply,
  updateAdminComment,
  getMyAdminAuthor,
  getCommunityPostDetail,
} from "../../api/domainApi";
import { AdminCommentAuthor } from "../../types/domain";
import AdminAuthorModal from "./AdminAuthorModal";

export type AdminCommentTarget =
  | { mode: "comment"; postId: number }
  | {
      mode: "reply";
      parentCommentId: number;
      postId: number;
      /** 무엇에 답하는지 보이도록 원 댓글을 같이 넘긴다 */
      parentContent?: string;
      parentAuthor?: string;
    }
  | { mode: "edit"; commentId: number; initialContent: string };

/**
 * 관리자 댓글/답글 작성·수정.
 *
 * 작성 명의는 로그인한 관리자 본인 프로필이라 여기서 고르지 않는다.
 * 프로필 미등록이면 서버가 404 로 막으므로, 열릴 때 미리 확인해 등록을 유도한다.
 *
 * 🔑 대상(게시글 본문 / 원 댓글)을 항상 함께 보여준다 — 무엇에 답하는지 모르고
 * 쓰게 되는 게 이 화면의 가장 큰 문제였다.
 */
const AdminCommentModal: React.FC<{
  open: boolean;
  target: AdminCommentTarget | null;
  onClose: () => void;
  onDone?: () => void;
}> = ({ open, target, onClose, onDone }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [author, setAuthor] = useState<AdminCommentAuthor | null>(null);
  const [authorChecked, setAuthorChecked] = useState(false);
  const [authorModalOpen, setAuthorModalOpen] = useState(false);

  const postId = target && target.mode !== "edit" ? target.postId : null;

  // 대상 게시글 — 제목/본문이 보여야 맥락을 알고 쓸 수 있다.
  const { data: post, isLoading: postLoading } = useSWR(
    open && postId ? `/community/posts/${postId}` : null,
    () => getCommunityPostDetail(postId!),
  );

  useEffect(() => {
    if (!open) return;
    setAuthorChecked(false);
    // 미등록이면 404 — 에러가 아니라 "등록 유도" 신호다.
    getMyAdminAuthor()
      .then(setAuthor)
      .catch(() => setAuthor(null))
      .finally(() => setAuthorChecked(true));
    form.setFieldsValue({
      content: target?.mode === "edit" ? target.initialContent : "",
    });
  }, [open, target, form]);

  const title =
    target?.mode === "edit"
      ? "관리자 댓글 수정"
      : target?.mode === "reply"
        ? "관리자 답글 작성"
        : "관리자 댓글 작성";

  const submit = async () => {
    if (!target) return;
    const v = await form.validateFields();
    const body = { content: v.content.trim() };
    setSaving(true);
    try {
      if (target.mode === "edit") await updateAdminComment(target.commentId, body);
      else if (target.mode === "reply") await createAdminReply(target.parentCommentId, body);
      else await createAdminComment(target.postId, body);

      message.success(target.mode === "edit" ? "수정되었습니다." : "등록되었습니다.");
      onDone?.();
      onClose();
    } catch (e: any) {
      message.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const needsProfile = authorChecked && !author;

  return (
    <>
      <Modal
        open={open}
        title={title}
        onCancel={onClose}
        onOk={submit}
        okText={target?.mode === "edit" ? "수정" : "등록"}
        cancelText="취소"
        confirmLoading={saving}
        okButtonProps={{ disabled: needsProfile }}
        width={640}
        destroyOnClose
      >
        {needsProfile ? (
          <Alert
            type="warning"
            showIcon
            message="먼저 관리자 프로필을 등록해 주세요."
            description={
              <Space direction="vertical">
                <span>
                  사용자 화면에 표시될 관리자명과 사진이 있어야 댓글을 쓸 수 있습니다.
                </span>
                <Button type="primary" size="small" onClick={() => setAuthorModalOpen(true)}>
                  프로필 등록하기
                </Button>
              </Space>
            }
          />
        ) : (
          <>
            {/* 대상 컨텍스트 — 무엇에 답하는지 */}
            {postId != null && (
              <div
                style={{
                  border: "1px solid #f0f0f0",
                  borderRadius: 6,
                  padding: "10px 12px",
                  marginBottom: 12,
                  background: "#fafafa",
                }}
              >
                {postLoading ? (
                  <Spin size="small" />
                ) : (
                  <>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      대상 게시글 #{postId}
                    </Typography.Text>
                    <div style={{ fontWeight: 600, margin: "2px 0 4px" }}>
                      {post?.title ?? "(제목 없음)"}
                    </div>
                    <div
                      style={{
                        whiteSpace: "pre-wrap",
                        fontSize: 13,
                        color: "#595959",
                        maxHeight: 140,
                        overflowY: "auto",
                      }}
                    >
                      {post?.content ?? ""}
                    </div>
                  </>
                )}
              </div>
            )}

            {target?.mode === "reply" && (
              <div
                style={{
                  borderLeft: "3px solid #d9d9d9",
                  padding: "4px 0 4px 10px",
                  marginBottom: 12,
                }}
              >
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  답글 대상 댓글 #{target.parentCommentId}
                  {target.parentAuthor ? ` · ${target.parentAuthor}` : ""}
                </Typography.Text>
                <div style={{ whiteSpace: "pre-wrap", fontSize: 13 }}>
                  {target.parentContent ?? "(내용 없음)"}
                </div>
              </div>
            )}

            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={
                <span>
                  작성 명의: <b>{author?.displayName ?? "-"}</b>
                </span>
              }
              description="사용자 화면에는 일반 댓글과 똑같이 보이며, 반려견 정보는 표시되지 않습니다."
            />
            <Form form={form} layout="vertical">
              <Form.Item
                name="content"
                label="내용"
                rules={[
                  { required: true, message: "내용을 입력해 주세요." },
                  { max: 1000, message: "최대 1000자입니다." },
                ]}
              >
                <Input.TextArea rows={5} maxLength={1000} showCount />
              </Form.Item>
            </Form>
            {target?.mode !== "edit" && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                게시글 작성자에게 알림이 발송됩니다.
              </Typography.Text>
            )}
          </>
        )}
      </Modal>

      <AdminAuthorModal
        open={authorModalOpen}
        onClose={() => setAuthorModalOpen(false)}
        onSaved={(a) => setAuthor(a)}
      />
    </>
  );
};

export default AdminCommentModal;
